/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType, lazy, useRef, createElement } from 'react'

import {
  TFileToRouteMap,
  PreloadableComponent,
  PreloadableRouteObject,
} from '~/app/models'
import {
  EAdminPortalClientPath,
  EClientPath,
  EMarketingClientPath,
} from '~/models/api'

export function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): PreloadableComponent<T> {
  const ReactLazyComponent = lazy(factory)
  let PreloadedComponent: T | undefined
  let factoryPromise: Promise<T> | undefined

  function Component(props: any) {
    const ComponentToRender = useRef(PreloadedComponent ?? ReactLazyComponent)
    return createElement(ComponentToRender.current, props)
  }

  const LazyWithPreload = Component as unknown as PreloadableComponent<T>

  LazyWithPreload.preload = () => {
    if (!factoryPromise) {
      factoryPromise = factory().then((module) => {
        PreloadedComponent = module.default
        return PreloadedComponent
      })
    }
    return factoryPromise
  }

  return LazyWithPreload
}

export const getLazyComponent = (
  fileToRouteMap: TFileToRouteMap,
  getImportPromise: () => Promise<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    default: PreloadableComponent<any>
  }>,
  shouldAvoidPreloadingCascade: boolean = false,
) =>
  lazyWithPreload(() => {
    const CASCADE_PRELOADING_DELAY = 3000

    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      if (!shouldAvoidPreloadingCascade && preloadUnloadedComponentsFromMap) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        preloadUnloadedComponentsFromMap(fileToRouteMap)
      }
    }, CASCADE_PRELOADING_DELAY)

    const importedPromise = getImportPromise()

    return importedPromise
  })

const preloadedComponentsPaths: (
  | EClientPath
  | EMarketingClientPath
  | EAdminPortalClientPath
)[] = []

const preloadUnloadedComponentsFromMap = (
  fileToRouteMap: TFileToRouteMap,
): void => {
  Object.values(fileToRouteMap)
    .filter(
      (route) => !preloadedComponentsPaths.some((path) => path === route.path),
    )
    .forEach((route) => {
      const isntYetLoaded = !preloadedComponentsPaths.some(
        (path) => path === route.path,
      )
      if (isntYetLoaded) {
        preloadedComponentsPaths.push(route.path)

        if (route.getImportPromise) {
          getLazyComponent(
            fileToRouteMap,
            route.getImportPromise,
            true,
          ).preload()
        }

        if (route.children) {
          preloadUnloadedComponentsFromMap(route.children)
        }
      }
    })
}

export const getRoutesFromMap = (
  fileToRouteMap: TFileToRouteMap,
): PreloadableRouteObject[] =>
  Object.values(fileToRouteMap).map((route) => {
    const Component = route.getImportPromise
      ? getLazyComponent(fileToRouteMap, route.getImportPromise)
      : undefined

    return {
      ...route,
      element: Component ? <Component /> : undefined,
      preload: Component?.preload,
      children: route.children ? getRoutesFromMap(route.children) : [],
    } as unknown as PreloadableRouteObject
  })

export default getLazyComponent
